import { z } from 'zod';
import { connectedHostSchema } from '../macroEditor/hostSelection';
import { macroTriggerSchema } from '../macroTree';
import { macroAuthoringDocumentSchema } from './authoring';
import { savedMacroSchema } from './authoringFlow';
import { authoringStageSchema } from './authoringPerformance';
import { canonicalJson, sha256Hex, sha256HexSchema } from './canonicalJson';
import { macroPublicationRequestSchema, macroPublicationResultSchema } from './publication';

const counter = z.number().int().nonnegative().safe();
const campaign = z.string().min(1).max(128);
export const authoringOperationCommandSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('compile'), document: macroAuthoringDocumentSchema,
    expectedStoredRev: counter, hostCampaignId: campaign }).strict(),
  z.object({ kind: z.literal('publish'), campaignId: campaign, trigger: macroTriggerSchema,
    publication: macroPublicationRequestSchema }).strict(),
]);
export type AuthoringOperationCommand = z.infer<typeof authoringOperationCommandSchema>;

export const authoringOperationStartSchema = z.object({
  operationId: z.string().uuid(), selectedHost: connectedHostSchema,
  command: authoringOperationCommandSchema,
}).strict().superRefine((input, context) => {
  if (input.command.kind === 'publish' && input.operationId !== input.command.publication.operationId) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['operationId'],
      message: 'Publication must use the authoring operation identity' });
  }
});
export type AuthoringOperationStart = z.infer<typeof authoringOperationStartSchema>;

export const authoringOperationResultSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('compile'), saved: savedMacroSchema }).strict(),
  z.object({ kind: z.literal('publish'), publication: macroPublicationResultSchema }).strict(),
]);
export type AuthoringOperationResult = z.infer<typeof authoringOperationResultSchema>;

export const authoringOperationOutcomeSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('complete'), result: authoringOperationResultSchema }).strict(),
  z.object({ status: z.literal('failed'), code: z.string().regex(/^[a-z0-9_]{1,128}$/),
    message: z.string().min(1).max(1024) }).strict(),
  z.object({ status: z.literal('cancelled') }).strict(),
]);
export type AuthoringOperationOutcome = z.infer<typeof authoringOperationOutcomeSchema>;

const state = z.object({
  operationId: z.string().uuid(), kind: z.enum(['compile', 'publish']), subjectDigest: sha256HexSchema,
  sequence: counter, createdAtUnixMs: counter, deadlineUnixMs: counter, heartbeatAtUnixMs: counter,
  stage: authoringStageSchema, completedUnits: counter, totalUnits: counter.nullable(),
}).strict();
export const authoringOperationStateSchema = z.discriminatedUnion('phase', [
  state.extend({ phase: z.literal('running') }).strict(),
  state.extend({ phase: z.literal('waiting-host') }).strict(),
  state.extend({ phase: z.literal('cancelling') }).strict(),
  state.extend({ phase: z.literal('settled'), outcome: authoringOperationOutcomeSchema }).strict(),
]).superRefine((value, context) => {
  const fail = (path: string, message: string) =>
    context.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
  if (value.totalUnits !== null && value.completedUnits > value.totalUnits) {
    fail('completedUnits', 'Completed work must not exceed the known total');
  }
  if (value.deadlineUnixMs <= value.createdAtUnixMs || value.heartbeatAtUnixMs < value.createdAtUnixMs) {
    fail('deadlineUnixMs', 'Operation deadline and heartbeat must follow creation');
  }
  if (value.phase === 'settled' && value.outcome.status === 'complete'
    && value.outcome.result.kind !== value.kind) fail('outcome', 'Result kind must match the operation');
});
export type AuthoringOperationState = z.infer<typeof authoringOperationStateSchema>;

export const authoringOperationReauthorizationSchema = z.object({ selectedHost: connectedHostSchema }).strict();
export type AuthoringOperationReauthorization = z.infer<typeof authoringOperationReauthorizationSchema>;
export const authoringOperationCancelSchema = z.object({}).strict();
export const AUTHORING_OPERATION_ROUTES = {
  start: { method: 'POST', path: '/api/macro/authoring/operations' },
  status: { method: 'GET', path: '/api/macro/authoring/operations/:operationId' },
  cancel: { method: 'POST', path: '/api/macro/authoring/operations/:operationId/cancel' },
  reauthorize: { method: 'POST', path: '/api/macro/authoring/operations/:operationId/host' },
} as const;

/** Injected defaults; deadlines are absolute and must not restart on polling or takeover. */
export const AUTHORING_OPERATION_POLICY = Object.freeze({
  overallDeadlineMs: 15 * 60_000, ownerLeaseMs: 30_000, heartbeatIntervalMs: 10_000,
  terminalRetentionMs: 24 * 60 * 60_000, storageConcurrency: 4, decoderConcurrency: 2,
});

/** Transient control credentials are excluded; every authored command field remains pinned. */
export async function authoringOperationSubjectDigest(command: AuthoringOperationCommand, machineId: string): Promise<string> {
  const subject = { version: 1, command: authoringOperationCommandSchema.parse(command),
    machineId: z.string().min(1).max(256).parse(machineId) };
  return sha256Hex(canonicalJson(subject));
}
